import React from 'react'
import AdminSidebar from "../Sidebar/AdminSidebar";
import { useNavigate } from 'react-router-dom';
import { SuperAdminApis } from '../../apis/superAdminApi';
import { AdminSmsApi } from '../../apis/AdminSmsApi';

interface StatCardProps {
  label: string;
  value: string | number;
  sub?: string;
  color: string;
  icon: React.ReactNode;
}

function StatCard({ label, value, sub, color, icon }: StatCardProps) {
  return (
    <div className={`${color} rounded-xl p-5 text-white shadow-sm`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium opacity-80">{label}</p>
          <p className="text-3xl font-bold mt-1">{value}</p>
          {sub && <p className="text-xs opacity-70 mt-1">{sub}</p>}
        </div>
        <div className="opacity-80 mt-1">{icon}</div>
      </div>
    </div>
  );
}

interface QuickLinkProps {
  label: string;
  desc: string;
  path: string;
  color: string;
}

function QuickLink({ label, desc, path, color }: QuickLinkProps) {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(path)}
      className={`w-full text-left border-l-4 ${color} bg-white rounded-lg shadow-sm px-5 py-4 hover:shadow-md transition-shadow`}
    >
      <p className="font-semibold text-gray-800 text-sm">{label}</p>
      <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
    </button>
  );
}

function fmt(n: any) {
  const num = parseFloat(n ?? 0);
  return isNaN(num) ? '0' : num.toLocaleString();
}

function Dashboard() {
  const [userCount, setUserCount] = React.useState<any>({});
  const [smsStats, setSmsStats]   = React.useState<any>({});
  const [loading, setLoading]     = React.useState(true);

  React.useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [linksRes, smsRes]: any[] = await Promise.all([
          SuperAdminApis.getlinksCount().catch(() => null),
          AdminSmsApi.getSmsDashboard().catch(() => null),
        ]);
        if (linksRes?.data?.data) setUserCount(linksRes.data.data);
        if (smsRes?.data)         setSmsStats(smsRes.data);
      } catch {}
      setLoading(false);
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div>
        <AdminSidebar />
        <div className="relative md:ml-64 bg-gray-50 min-h-screen flex items-center justify-center">
          <div className="flex flex-col items-center gap-3 text-gray-400">
            <div className="w-8 h-8 border-4 border-[#0071BC] border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm">Loading dashboard…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <AdminSidebar />
      <div className="relative md:ml-64 bg-gray-50 min-h-screen">
        <div className="py-8 lg:py-12 lg:px-10 px-6">

          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-800">Admin Dashboard</h1>
            <p className="text-sm text-gray-500 mt-1">Platform overview and quick actions</p>
          </div>

          {/* ── Users & Links ── */}
          <section className="mb-8">
            <h2 className="text-base font-semibold text-gray-700 mb-4 uppercase tracking-wide">Users & Links</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                label="Total Users"
                value={fmt(userCount.total_users)}
                color="bg-gradient-to-br from-[#0071BC] to-blue-400"
                icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>}
              />
              <StatCard
                label="Total Clicks"
                value={fmt(userCount.total_Clicks)}
                color="bg-gradient-to-br from-green-500 to-teal-400"
                icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5"/></svg>}
              />
              <StatCard
                label="WhatsApp Links"
                value={fmt(userCount.total_whatsapp_link)}
                color="bg-gradient-to-br from-emerald-500 to-green-400"
                icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/></svg>}
              />
              <StatCard
                label="Redirect Links"
                value={fmt(userCount.total_redirect_link)}
                color="bg-gradient-to-br from-purple-500 to-pink-400"
                icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/></svg>}
              />
            </div>
          </section>

          {/* ── SMS Platform ── */}
          <section className="mb-8">
            <h2 className="text-base font-semibold text-gray-700 mb-4 uppercase tracking-wide">SMS Platform</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                label="Total SMS Sent"
                value={fmt(smsStats.total_sms_sent)}
                sub={`Last 7 days: ${fmt(smsStats.recent_sms_activity)}`}
                color="bg-gradient-to-br from-cyan-600 to-cyan-400"
                icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>}
              />
              <StatCard
                label="SMS Revenue"
                value={`₦${fmt(smsStats.total_amount_spent ?? smsStats.total_sms_revenue)}`}
                sub="Platform earnings"
                color="bg-gradient-to-br from-orange-500 to-yellow-400"
                icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>}
              />
              <StatCard
                label="SMS Users"
                value={fmt(smsStats.total_sms_users)}
                sub="Active wallets"
                color="bg-gradient-to-br from-indigo-500 to-purple-400"
                icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/></svg>}
              />
              <StatCard
                label="Delivery Rate"
                value={`${smsStats.delivery_rate ?? 0}%`}
                sub="Success rate"
                color="bg-gradient-to-br from-rose-500 to-pink-400"
                icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>}
              />
            </div>
          </section>

          {/* ── Sender IDs & Wallets ── */}
          <section className="mb-8">
            <h2 className="text-base font-semibold text-gray-700 mb-4 uppercase tracking-wide">Sender IDs & Wallets</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <StatCard
                label="Active Sender IDs"
                value={fmt(smsStats.active_sender_ids)}
                sub="Approved for use"
                color="bg-gradient-to-br from-blue-600 to-indigo-400"
                icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2"/></svg>}
              />
              <StatCard
                label="Pending Approvals"
                value={fmt(smsStats.pending_sender_ids)}
                sub="Awaiting review"
                color={`${(smsStats.pending_sender_ids ?? 0) > 0 ? 'bg-gradient-to-br from-orange-500 to-red-400' : 'bg-gradient-to-br from-gray-400 to-gray-500'}`}
                icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>}
              />
              <StatCard
                label="Total Wallet Balance"
                value={`₦${fmt(smsStats.total_wallet_balance)}`}
                sub="Across all users"
                color="bg-gradient-to-br from-teal-500 to-green-400"
                icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"/></svg>}
              />
            </div>
          </section>

          {/* ── Other Stats ── */}
          <section className="mb-8">
            <h2 className="text-base font-semibold text-gray-700 mb-4 uppercase tracking-wide">Other Statistics</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {[
                { label: 'Multi Links',    value: userCount.total_multi_link  ?? 0 },
                { label: 'Market Links',   value: userCount.total_market_link ?? 0 },
                { label: 'Total Products', value: userCount.total_products    ?? 0 },
              ].map(({ label, value }) => (
                <div key={label} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                  <p className="text-sm text-gray-500">{label}</p>
                  <p className="text-3xl font-bold text-gray-800 mt-1">{fmt(value)}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ── Quick Actions ── */}
          <section>
            <h2 className="text-base font-semibold text-gray-700 mb-4 uppercase tracking-wide">Quick Actions</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <QuickLink path="/admin-sender-ids" label="Sender ID Approvals" desc={`${fmt(smsStats.pending_sender_ids)} pending review`}    color="border-orange-400" />
              <QuickLink path="/admin-sms-users"  label="SMS Users"           desc="Manage wallets & balances"                                color="border-green-400"  />
              <QuickLink path="/witdrawal-request" label="Withdrawals"        desc="Process pending requests"                                 color="border-blue-400"   />
              <QuickLink path="/admin-users"       label="All Users"          desc={`${fmt(userCount.total_users)} registered users`}         color="border-purple-400" />
            </div>
          </section>

        </div>
      </div>
    </div>
  );
}

export default Dashboard;
